import { Link } from 'react-router-dom'
import { PASSES } from '../data/passes'
import { IllustrationSlot } from '../components/IllustrationSlot'
import { MapDots } from '../components/MapDots'
import { PassChip } from '../components/PassChip'
import { formatMoney } from '../lib/roi'
import { useStore } from '../lib/state'
import { resortById } from '../data/resorts'

export function Dashboard() {
  const { state, setProfile, setStamp, metrics } = useStore()
  const recent = [...state.days]
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 5)

  const hasPass = metrics.passPrice > 0
  const cpdLabel = metrics.cpd ? formatMoney(metrics.cpd) : '—'
  const needsStamp = metrics.visited === 0 && metrics.want === 0
  const needsDay = metrics.days === 0
  const statusHint = !hasPass
    ? 'Pick a pass to track value'
    : metrics.days === 0
      ? 'Log your first day to unlock $/day'
      : metrics.cpd && metrics.cpd < 80
        ? 'Paying for itself'
        : 'Keep logging days'

  return (
    <div className="stack">
      <section className="status-hero">
        <IllustrationSlot kind="mountain" className="illus-hero" caption="art soon" />
        <div className="status-kicker">
          <span className="season-tag">Season {state.profile.seasonLabel}</span>
          <PassChip id={state.profile.passId} />
        </div>
        <div className="status-label">Cost per day</div>
        <div className={`status-value${metrics.cpd ? '' : ' empty'}`}>{cpdLabel}</div>
        <div className="status-sub">
          <span className={`status-pill${metrics.cpd && metrics.cpd < 80 ? '' : ' warn'}`}>
            {statusHint}
          </span>
          {hasPass && (
            <span>
              {formatMoney(metrics.passPrice)} pass
              {metrics.days > 0 ? ` · ${metrics.days} day${metrics.days === 1 ? '' : 's'}` : ''}
            </span>
          )}
        </div>
      </section>

      <section className="quick-actions" aria-label="Primary actions">
        <Link className={`quick-action${needsStamp ? ' pulse' : ''}`} to="/mountains">
          <span className="qa-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <path d="M4 18 L10 7 l3.5 5.5 L16 9 l4 9 H4z" strokeLinejoin="round" />
            </svg>
          </span>
          <strong>Stamp a mountain</strong>
          <span>Mark where you’ve skied or want to go</span>
        </Link>
        <Link className={`quick-action${needsDay && hasPass ? ' pulse' : ''}`} to="/days">
          <span className="qa-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="4" y="5" width="16" height="15" rx="2.5" />
              <path d="M8 3v4M16 3v4M4 10h16" strokeLinecap="round" />
            </svg>
          </span>
          <strong>Log a ski day</strong>
          <span>Drop your cost-per-day live</span>
        </Link>
        <Link className="quick-action" to="/trips">
          <span className="qa-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <circle cx="9" cy="8" r="2.5" />
              <circle cx="16" cy="10" r="2.2" />
              <path d="M4.5 18c.8-2.4 2.6-3.6 4.5-3.6s3.7 1.2 4.5 3.6" strokeLinecap="round" />
            </svg>
          </span>
          <strong>Create a trip</strong>
          <span>Invite friends · set availability</span>
        </Link>
        <Link className="quick-action" to="/season">
          <span className="qa-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
              <rect x="3.5" y="5" width="17" height="14" rx="2.5" />
              <path d="M3.5 10h17" strokeLinecap="round" />
            </svg>
          </span>
          <strong>Share season card</strong>
          <span>Show off your passport</span>
        </Link>
      </section>

      <section className="hero">
        <div className="card stack">
          <div className="row between">
            <h3 style={{ margin: 0 }}>At a glance</h3>
            <Link className="btn sm ghost" to="/season">
              Share card →
            </Link>
          </div>
          <div className="metric-grid">
            <div className="metric">
              <div className="label">Visited</div>
              <div className="value" style={{ color: 'var(--good)' }}>
                {metrics.visited}
              </div>
              <div className="sub">mountains stamped</div>
            </div>
            <div className="metric">
              <div className="label">Wishlist</div>
              <div className="value" style={{ color: 'var(--want)' }}>
                {metrics.want}
              </div>
              <div className="sub">want to go</div>
            </div>
            <div className="metric">
              <div className="label">Ski days</div>
              <div className="value">{metrics.days}</div>
              <div className="sub">logged this season</div>
            </div>
            <div className="metric">
              <div className="label">Pass</div>
              <div className="value" style={{ fontSize: 18, letterSpacing: '-0.03em' }}>
                {PASSES.find((p) => p.id === state.profile.passId)?.short ?? '—'}
              </div>
              <div className="sub">{hasPass ? formatMoney(metrics.passPrice) : 'not set'}</div>
            </div>
          </div>
        </div>

        <div className="card stack profile-card">
          <h3 style={{ margin: 0 }}>Profile</h3>
          <div className="field">
            <label>Display name</label>
            <input
              placeholder="Your name"
              value={state.profile.displayName}
              onChange={(e) => setProfile({ displayName: e.target.value })}
            />
          </div>
          <div className="field">
            <label>Your pass</label>
            <select
              value={state.profile.passId}
              onChange={(e) => setProfile({ passId: e.target.value as typeof state.profile.passId })}
            >
              {PASSES.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.label}
                  {p.priceUsd ? ` · $${p.priceUsd}` : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="pass-preview">
            <PassChip id={state.profile.passId} />
            <span className="faint" style={{ fontSize: 12, lineHeight: 1.4 }}>
              {PASSES.find((p) => p.id === state.profile.passId)?.blurb}
            </span>
          </div>
        </div>
      </section>

      <section className="card stack">
        <div className="row between">
          <div>
            <h3 style={{ margin: 0 }}>Stamp map</h3>
            <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
              Your mountains on a North America map — tap to stamp
            </p>
          </div>
        </div>
        <MapDots stamps={state.stamps} onStamp={(id, status) => setStamp(id, status)} />
      </section>

      <section className="card stack">
        <div className="row between">
          <h3 style={{ margin: 0 }}>Recent days</h3>
          <Link className="btn sm primary" to="/days">
            Log a day
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="empty">
            No days yet —{' '}
            <Link to="/days" style={{ color: 'var(--accent)', fontWeight: 700 }}>
              log your first ski day
            </Link>
            .
            <IllustrationSlot kind="snowflake" className="illus-inline" caption="drawing soon" />
          </div>
        ) : (
          <div className="list">
            {recent.map((d) => (
              <div className="list-item" key={d.id}>
                <div className="row" style={{ gap: 12 }}>
                  <span className="day-item-dot" />
                  <div>
                    <div style={{ fontWeight: 700 }}>{resortById(d.resortId)?.name ?? d.resortId}</div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {d.date}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  )
}
